import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Globe, Facebook, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const TOPICS = ["Buying", "Selling", "Market Report", "Feature my business", "General"];

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", topic: "Buying", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      trackEvent("generate_lead", { method: "contact_form", topic: form.topic });
      toast.success("Message sent — Joselle will be in touch shortly.");
      setForm({ name: "", email: "", phone: "", topic: "Buying", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again or email directly.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl bg-[#0D0B0E]/70 border border-[#E890A7]/20 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#E890A7]/70 focus:ring-1 focus:ring-[#E890A7]/40 transition-colors";

  return (
    <section id="contact" data-testid="contact-section" className="relative pt-10 pb-28 sm:pt-14 sm:pb-36 bg-[#0D0B0E] overflow-hidden">
      <div className="absolute right-0 bottom-0 w-[28rem] h-[28rem] rounded-full bg-[#E890A7]/8 blur-[120px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
        >
          <p className="font-mono text-xs tracking-[0.35em] uppercase text-[#E890A7]/80 mb-4">07 · Contact</p>
          <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-tight">
            Begin the <em className="italic text-[#E890A7]">conversation</em>
          </h2>
          <p className="mt-6 text-slate-400 font-light max-w-md">
            Buying, selling, or simply curious what your shoreline is worth? Reach out — every
            enquiry lands directly with Joselle.
          </p>

          <div className="mt-10 space-y-5">
            <a data-testid="contact-phone-link" href="tel:+14167273558" onClick={() => trackEvent("contact_call", { source: "contact_section" })} className="flex items-center gap-4 group">
              <span className="w-11 h-11 rounded-full border border-[#E890A7]/30 flex items-center justify-center text-[#E890A7] group-hover:bg-[#E890A7] group-hover:text-[#0D0B0E] transition-all duration-300">
                <Phone size={17} />
              </span>
              <span className="text-slate-200 group-hover:text-[#F2A7BA] transition-colors">(416) 727-3558</span>
            </a>
            <a data-testid="contact-email-link" href="mailto:joselle@lagooncityrealestate.ca" className="flex items-center gap-4 group">
              <span className="w-11 h-11 rounded-full border border-[#E890A7]/30 flex items-center justify-center text-[#E890A7] group-hover:bg-[#E890A7] group-hover:text-[#0D0B0E] transition-all duration-300">
                <Mail size={17} />
              </span>
              <span className="text-slate-200 group-hover:text-[#F2A7BA] transition-colors">
                joselle@lagooncityrealestate.ca
              </span>
            </a>
            <a
              data-testid="contact-website-link"
              href="https://www.lagooncityrealestate.ca"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-4 group"
            >
              <span className="w-11 h-11 rounded-full border border-[#E890A7]/30 flex items-center justify-center text-[#E890A7] group-hover:bg-[#E890A7] group-hover:text-[#0D0B0E] transition-all duration-300">
                <Globe size={17} />
              </span>
              <span className="text-slate-200 group-hover:text-[#F2A7BA] transition-colors">www.lagooncityrealestate.ca</span>
            </a>
            <a
              data-testid="contact-facebook-link"
              href="https://www.facebook.com/profile.php?id=61589628669251"
              target="_blank"
              rel="noreferrer"
              onClick={() => trackEvent("contact_social", { source: "contact_section", network: "facebook" })}
              className="flex items-center gap-4 group"
            >
              <span className="w-11 h-11 rounded-full border border-[#E890A7]/30 flex items-center justify-center text-[#E890A7] group-hover:bg-[#E890A7] group-hover:text-[#0D0B0E] transition-all duration-300">
                <Facebook size={17} />
              </span>
              <span className="text-slate-200 group-hover:text-[#F2A7BA] transition-colors">Follow on Facebook</span>
            </a>
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-full border border-[#E890A7]/30 flex items-center justify-center text-[#E890A7]">
                <MapPin size={17} />
              </span>
              <span className="text-slate-200">Lagoon City, Ontario — on the canals</span>
            </div>
          </div>

        </motion.div>

        <motion.form
          data-testid="contact-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="rounded-[2rem] border border-[#E890A7]/20 bg-[#151218]/80 backdrop-blur-xl p-8 sm:p-10 h-fit"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <input
              data-testid="contact-name-input"
              required
              value={form.name}
              onChange={set("name")}
              placeholder="Your name"
              className={inputCls}
            />
            <input
              data-testid="contact-email-input"
              required
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder="Email address"
              className={inputCls}
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <input
              data-testid="contact-phone-input"
              value={form.phone}
              onChange={set("phone")}
              placeholder="Phone (optional)"
              className={inputCls}
            />
            <select
              data-testid="contact-topic-select"
              value={form.topic}
              onChange={set("topic")}
              className={`${inputCls} appearance-none`}
            >
              {TOPICS.map((t) => (
                <option key={t} value={t} className="bg-[#151218]">
                  {t}
                </option>
              ))}
            </select>
          </div>
          <textarea
            data-testid="contact-message-input"
            required
            rows={5}
            value={form.message}
            onChange={set("message")}
            placeholder="Tell Joselle what you're dreaming of…"
            className={`${inputCls} mt-4 resize-none`}
          />
          <button
            data-testid="contact-submit-btn"
            type="submit"
            disabled={sending}
            className="mt-6 w-full rounded-full bg-[#E890A7] text-[#0D0B0E] font-semibold px-7 py-4 flex items-center justify-center gap-2 hover:bg-[#F2A7BA] disabled:opacity-60 transition-all duration-300"
          >
            {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} />}
            {sending ? "Sending…" : "Send to Joselle"}
          </button>
          <p className="mt-4 text-center text-[11px] text-slate-600">
            Your details stay private — no spam, ever.
          </p>
        </motion.form>
      </div>
    </section>
  );
}
